/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this migration uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (the
 * page-with-back-button + FormGroup field layout, and the parent-picker idiom this screen needs).
 *
 * Only the data, columns and labels are ours. Reuses the same sources brand's own New.tsx cites
 * (screen-map.md row 11).
 */
import { useState } from "react";
import { useLoaderData, useOutletContext } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import {
  PiIdentificationBadgeDuotone as DetailsIcon,
  PiImageDuotone as ImageIcon,
  PiTreeStructureDuotone as PlacementIcon,
  PiXBold as RemoveIcon,
} from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import Upload from "@core/ui/upload";
import { imageUrl, useFileUpload, type UploadedFile } from "@steelyard/common";
import { createCategory, listCategoryTree, type Category } from "../../../lib/services/catalog";
import { can } from "../../../lib/permissions";
import { formCard, formCardFooter, formSections } from "../../../lib/form-layout";
import { SectionTitle } from "../../../components/section-title";
import type { AccountSummary, PermissionState } from "../../../types";
import { PageHeader, ErrorSurface, problemTitle, Field, FormGroup, Form } from "@steelyard/theme";
import { SelectField } from "../../../components/select-field";
import { ActionIcon, Text } from "rizzui";
import { UploadDropHint, UploadBusyHint } from "../../../components/upload-drop-hint";
import { useGoBack } from "../../../lib/navigation";

/**
 * Active categories only in the parent picker (project owner, 2026-09-29). The backend still
 * accepts an inactive parent — this is what the form offers, not a rule the client enforces.
 */
export async function newCategoryLoader() {
  const tree = await listCategoryTree(true);
  if (!tree) throw new Error("The API returned no body for the category tree.");
  return { tree };
}

const categorySchema = z.object({
  name: z.string().trim().min(1, "Required").max(200),
  code: z.string().trim().min(1, "Required").max(50),
  // A native <select> value is always a string; "" means "no parent (top-level)".
  parentId: z.string().trim().optional().or(z.literal("")),
  sortOrder: z
    .string()
    .trim()
    .regex(/^\d+$/, "Enter a whole number")
    .optional()
    .or(z.literal("")),
});
type CategoryFormValues = z.infer<typeof categorySchema>;

export default function NewCategory() {
  const goBack = useGoBack("/catalogue/categories");
  const { tree } = useLoaderData() as Awaited<ReturnType<typeof newCategoryLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [imageHash, setImageHash] = useState<string | null>(null);
  const [uploadedImage, setUploadedImage] = useState<UploadedFile | null>(null);
  const image = useFileUpload("image", {
    onUploaded: (result) => {
      setUploadedImage(result);
      setImageHash(result.hash);
      toast.success("Image uploaded");
    },
    onError: () => toast.error("Could not upload the image"),
  });
  const [submitError, setSubmitError] = useState<unknown>(null);

  if (!can(permissionState, "catalogue.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="create a category" />;
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title="New Category"
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue · Categories", href: "/catalogue/categories" }, { name: "New Category" }]}
      />

      <Form<CategoryFormValues>
        validationSchema={categorySchema}
        useFormProps={{ defaultValues: { name: "", code: "", parentId: "", sortOrder: "0" } }}
        className={formCard}
        onSubmit={async (values) => {
          setSubmitError(null);
          try {
            await createCategory({
              name: values.name,
              code: values.code,
              parentId: values.parentId || null,
              sortOrder: Number(values.sortOrder || 0),
              imageHash,
            });
            toast.success("Category created");
            goBack();
          } catch (err) {
            // DEF-06: a toast alone was missing on a fast rejection (a 409 duplicate code,
            // most often — it can resolve almost immediately). This surfaces the same
            // failure as a message that stays on the page until the next submit.
            setSubmitError(err);
            toast.error(problemTitle(err, "Could not create the category"));
          }
        }}
      >
        {({ register, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              {submitError ? <ErrorSurface error={submitError} action="create this category" /> : null}
              <FormGroup title={<SectionTitle icon={DetailsIcon}>Details</SectionTitle>} description="The code is permanent once saved — it is what appears on price lists and in Tally.">
                <Field label="Name" required maxLength={200} autoComplete="off" error={errors.name?.message} {...register("name")} />
                <Field
                  label="Code"
                  required
                  maxLength={50}
                  autoComplete="off"
                  className="font-mono"
                  hint="Unique across all categories, and not editable later."
                  error={errors.code?.message}
                  {...register("code")}
                />
              </FormGroup>
              <FormGroup title={<SectionTitle icon={PlacementIcon}>Placement</SectionTitle>} description="Where this category sits in the tree, and its order among its siblings.">
                <SelectField label="Parent" hint="Leave as top-level, or file this under an existing category." error={errors.parentId?.message} {...register("parentId")}>
                  <option value="" data-choice>No parent (top-level category)</option>
                  {tree.items.map((c: Category) => (
                    <option key={c.id} value={c.id}>
                      {"  ".repeat(c.depth)}
                      {c.name}
                    </option>
                  ))}
                </SelectField>
                <Field
                  label="Sort order"
                  inputMode="numeric"
                  hint="Lower sorts first among siblings."
                  error={errors.sortOrder?.message}
                  {...register("sortOrder")}
                />
              </FormGroup>
              <FormGroup title={<SectionTitle icon={ImageIcon}>Image</SectionTitle>} description="Optional. Uploaded as a real file, not linked.">
                <div className="col-span-full flex flex-wrap items-center gap-4">
                  <Upload
                    accept="img"
                    multiple={false}
                    disabled={image.busy}
                    onChange={image.onChange}
                    aria-label="Category image file"
                    placeholderText={image.busy ? <UploadBusyHint label="Uploading…" /> : <UploadDropHint />}
                    iconClassName="size-8 shrink-0"
                    className="p-4"
                    wrapperClassName="w-full max-w-xs"
                  />
                  {imageHash ? (
                    <div className="flex flex-col items-start gap-1.5">
                      <div className="relative">
                        <img src={imageUrl(imageHash)} alt="Uploaded category image" className="h-20 w-20 rounded-lg border border-muted object-cover" />
                        <ActionIcon
                          type="button"
                          size="sm"
                          variant="solid"
                          color="danger"
                          className="absolute -right-2 -top-2"
                          aria-label="Remove this image"
                          onClick={() => {
                            setImageHash(null);
                            setUploadedImage(null);
                          }}
                        >
                          <RemoveIcon aria-hidden className="size-3.5" />
                        </ActionIcon>
                      </div>
                      {uploadedImage ? (
                        <Text className="text-xs text-gray-500">
                          {uploadedImage.sizeBytes.toLocaleString()} bytes · {uploadedImage.contentType}
                        </Text>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {image.error ? <ErrorSurface error={image.error} action="upload this image" /> : null}
              </FormGroup>
            </div>

            <FormFooter
              className={formCardFooter}
              submitBtnText={isSubmitting ? "Creating…" : "Create category"}
              isLoading={isSubmitting}
              altBtnText="Cancel"
              handleAltBtn={goBack}
            />
          </>
        )}
      </Form>
    </div>
  );
}
